import { useWalletDetails } from "@sundae/wallet-react";
import { EDatumType, IPoolData, IWithdrawConfigArgs } from "@sundaeswap/core";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useCustomCompareEffect, useCustomCompareMemo } from "use-custom-compare";
import { useMigrateLiquidityContext } from "../../components/Dialogs/MigrateLiquidityDialog/context";
import { useAssetsWithBalance } from "../useAssetsWithBalance";

export const useMigrateLiquidityConfig = () => {
  const {
    derived: { isLoadingV3Pools, selectedPools },
    handlers: { setIsMigrateLiquidityConfigEmpty },
  } = useMigrateLiquidityContext();
  const { lpAssets } = useAssetsWithBalance();
  const {
    details: { mainAddress },
  } = useWalletDetails();

  const migrateLiquidityConfig = useCustomCompareMemo(
    () =>
      selectedPools
        .map(({ V1: v1Pool, V3: v3Pool }) => {
          const matchingLpAssetBalance = lpAssets?.find(
            (asset) => asset.metadata.assetId === v1Pool.assetLP.assetId
          );

          if (!matchingLpAssetBalance) {
            return null;
          }

          return {
            withdrawConfig: {
              pool: {
                assetA: v1Pool.assetA,
                assetB: v1Pool.assetB,
                assetLP: v1Pool.assetLP,
                currentFee: Number(v1Pool.fee) / 100,
                ident: v1Pool.ident,
                liquidity: {
                  aReserve: BigInt(v1Pool.quantityA || 0),
                  bReserve: BigInt(v1Pool.quantityB || 0),
                  lpTotal: BigInt(v1Pool.quantityLP || 0),
                },
                version: v1Pool.version,
              },
              suppliedLPAsset: matchingLpAssetBalance?.quantity,
              orderAddresses: {
                DestinationAddress: {
                  address: mainAddress,
                  datum: {
                    type: EDatumType.NONE,
                  },
                },
              },
            } as IWithdrawConfigArgs,
            depositPool: {
              assetA: v3Pool.assetA,
              assetB: v3Pool.assetB,
              assetLP: v3Pool.assetLP,
              currentFee: Number(v3Pool.fee),
              liquidity: {
                aReserve: BigInt(v3Pool.quantityA || 0),
                bReserve: BigInt(v3Pool.quantityB || 0),
                lpTotal: BigInt(v3Pool.quantityLP || 0),
              },
              version: v3Pool.version,
              ident: v3Pool.ident,
            } as IPoolData,
          };
        })
        // filter nulls
        .filter(Boolean) as {
        withdrawConfig: IWithdrawConfigArgs;
        depositPool: IPoolData;
      }[],
    [selectedPools, lpAssets, mainAddress],
    isEqual
  );

  /**
   * This should not be necessary anymore once we fetch the v3 positions as well and compare them to the v1 positions
   */
  useCustomCompareEffect(
    () => {
      if (!isLoadingV3Pools) {
        setIsMigrateLiquidityConfigEmpty(isEmpty(migrateLiquidityConfig));
      }
    },
    [isLoadingV3Pools, migrateLiquidityConfig, setIsMigrateLiquidityConfigEmpty],
    isEqual
  );

  return migrateLiquidityConfig;
};
